// -------------------------------
// LEAVE INTERFACES
// -------------------------------
export interface PendingLeave {
    employeeId?: number;
    leaveTypeId?: number;
    fromDate?: string;
    toDate?: string;
    leaveMode?: "Full Day" | "Half Day";
    reason?: string;
}

export interface PendingDeleteLeave {
    awaitingEmployee?: boolean;
    awaitingDate?: boolean;
    employeeId?: number;
}

export const startCreateLeaveAdminFlow = (
    setPendingLeave: (data: PendingLeave | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    employeeIdFromIntent?: number
) => {
    if (employeeIdFromIntent) {
        setPendingLeave({ employeeId: employeeIdFromIntent });

        pushAIChat("Which leave type should I apply?");
        speak("Which leave type should I apply?");
        return;
    }

    setPendingLeave({});

    pushAIChat("Sure! For which employee should I create the leave?");
    speak("For which employee should I create the leave?");

    window.dispatchEvent(
        new CustomEvent("ai-suggest-options", {
            detail: { type: "employee" }
        })
    );
};

export const processPendingLeaveAdmin = (
  text: string,
  pendingLeave: PendingLeave,
  setPendingLeave: (data: PendingLeave | null) => void,
  pushAIChat: (msg: string) => void,
  speak: (msg: string) => void
) => {

  // STEP 3 — FROM DATE
  if (!pendingLeave.fromDate) {
    setPendingLeave({ ...pendingLeave, fromDate: text });

    pushAIChat("When should the leave end?");
    speak("When should the leave end?");
    return;
  }

  // STEP 4 — TO DATE
  if (!pendingLeave.toDate) {
    setPendingLeave({ ...pendingLeave, toDate: text });

    pushAIChat("Is this a Full Day or Half Day leave?");
    speak("Is this full day or half day?");
    return;
  }

  // STEP 5 — LEAVE MODE
  if (!pendingLeave.leaveMode) {
    const leaveMode =
      text.toLowerCase().includes("half") ? "Half Day" : "Full Day";

    setPendingLeave({ ...pendingLeave, leaveMode });

    pushAIChat("Please tell me the reason for this leave.");
    speak("What is the reason for this leave?");
    return;
  }

  // STEP 6 — REASON
  if (!pendingLeave.reason) {
    const reason = text.trim();
    if (!reason) return;

    window.dispatchEvent(
      new CustomEvent("ai-create-leave", {
        detail: {
          employeeId: pendingLeave.employeeId,
          leaveTypeId: pendingLeave.leaveTypeId,
          fromDate: pendingLeave.fromDate,
          toDate: pendingLeave.toDate,
          leaveMode: pendingLeave.leaveMode,
          reason
        }
      })
    );

    setPendingLeave(null);
  }
};



export const startDeleteLeaveAdminFlow = (
    setPendingDelete: (data: PendingDeleteLeave | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    employeeIdFromIntent?: number
) => {
    if (employeeIdFromIntent) {
        setPendingDelete({
            awaitingDate: true,
            employeeId: employeeIdFromIntent
        });

        pushAIChat("Which leave date should I delete?");
        speak("Which leave date should I delete?");
        return;
    }

    setPendingDelete({ awaitingEmployee: true });
    pushAIChat("For which employee should I delete the leave?");
    speak("For which employee?");
};

export const processPendingDeleteLeaveAdmin = (
    text: string,
    pendingDelete: PendingDeleteLeave,
    setPendingDelete: (data: PendingDeleteLeave | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — EMPLOYEE
    if (pendingDelete.awaitingEmployee) {
        const employeeId = Number(text);

        if (!employeeId) {
            pushAIChat("Please provide a valid employee ID.");
            speak("Please provide a valid employee.");
            return;
        }

        setPendingDelete({
            awaitingDate: true,
            employeeId
        });

        pushAIChat("Which leave date should I delete?");
        speak("Which leave date?");
        return;
    }

    // STEP 2 — DATE
    if (pendingDelete.awaitingDate && pendingDelete.employeeId) {
        const fromDate = text.trim();

        if (!fromDate) {
            pushAIChat("Please provide a valid date.");
            speak("Please provide a valid date.");
            return;
        }

        pushAIChat("Deleting leave request...");
        speak("Deleting leave request");

        window.dispatchEvent(
            new CustomEvent("ai-delete-leave", {
                detail: {
                    employeeId: pendingDelete.employeeId,
                    fromDate
                }
            })
        );

        setPendingDelete(null);
    }
};

